import type { PumpStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import { PumpAlgorithmState } from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"

import { getPumpAlgorithmState } from "./getPumpAlgorithmState"

import { usePolyglot } from "src/contexts"
import type { DataRecency } from "src/types"

const localizationPrefix = "components.pump_status.algorithm_status."

export interface Props extends ComponentPropsWithoutRef<"div"> {
  pumpStatus: PumpStatus;
  pumpStatusRecency: DataRecency;
}

export const PumpAlgorithmStatus = forwardRef<HTMLDivElement, Props>(
  function PumpAlgorithmStatus(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      pumpStatus,
      pumpStatusRecency,
      ...divProps
    } = props

    const polyglot = usePolyglot()

    const algorithmState = getPumpAlgorithmState(
      pumpStatus,
      pumpStatusRecency,
    )

    const algorithmStateString: string = useMemo(
      () => {
        switch (algorithmState) {
          case PumpAlgorithmState.NotAvailable:
            return polyglot.t(`${localizationPrefix}not_available`)
          case PumpAlgorithmState.Off:
            return polyglot.t(`${localizationPrefix}off`)
          case PumpAlgorithmState.Inactive:
            return polyglot.t(`${localizationPrefix}inactive`)
          case PumpAlgorithmState.Suspended:
          case PumpAlgorithmState.Reduced:
          case PumpAlgorithmState.Raised:
            return polyglot.t(`${localizationPrefix}active`)
          case PumpAlgorithmState.NoData:
          default:
            return "---"
        }
      },
      [
        algorithmState,
        polyglot,
      ],
    )

    return (
      <div
        {...divProps}
        ref={propRef}
      >
        {algorithmStateString}
      </div>
    )
  },
)
